import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { 
  Home, 
  BookOpen, 
  Gamepad2,
  Shield,
  Menu,
  ArrowRight,
  ShoppingCart,
  User as UserIcon,
  Rocket,
  Palette,
  Scan,
  Phone
} from "lucide-react";
import { XIcon as XSvg, InstagramIcon, WhatsAppIcon, TelegramIcon, YouTubeIcon } from "@/components/ui/SocialIcons";
import { TopDrawer } from "@/components/ui/top-drawer";
import { ThemeDialog } from "@/components/theme/ThemeDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QrCode } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";
import { AboutOwnerProfile } from "@/components/about/AboutOwnerProfile";
import { getDemoProfile } from "@/utils/demoProfile";
import { MetefyQrAuthDrawer } from "@/components/auth/MetefyQrAuthDrawer";

interface OtherPagesHeaderProps {
  hideOnScrollDown?: boolean;
}

export function OtherPagesHeader({ hideOnScrollDown = false }: OtherPagesHeaderProps = {}) {
  const { user } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const [isDemoProfileOpen, setIsDemoProfileOpen] = useState(false);
  const [isAuthDrawerOpen, setIsAuthDrawerOpen] = useState(false);
  const qrScannerInstanceRef = useRef<any>(null);

  const openDemoProfile = (profile: any) => {
    setIsDemoProfileOpen(true);
  };

  // Handle scroll effect - always enabled
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      
      // Hide on scroll down, show on scroll up
      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Handle browser back button for sidebar
  useEffect(() => {
    if (!isSheetOpen) return;
    const onPopState = (e: PopStateEvent) => {
      setIsSheetOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isSheetOpen]);

  // Handle browser back button for theme dialog
  useEffect(() => {
    if (!isThemeDialogOpen) return;
    const onPopState = (e: PopStateEvent) => {
      setIsThemeDialogOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isThemeDialogOpen]);

  const navigationItems = [
    { icon: Home, label: "Main", to: "/" },
    { icon: BookOpen, label: "Blog", to: "/blog" },
    { icon: Gamepad2, label: "Games", to: "/games" },
    { icon: Gamepad2, label: "Puzzle", to: "/puzzle" },
    { icon: Shield, label: "Privacy", to: "/privacy-policy" },
  ];

  const isActive = (to: string) => {
    return location.pathname === to;
  };

  const startQrScan = async () => {
    try {
      setShowScanner(true);
      setIsScanning(true);

      // Suppress console warnings from QR scanner
      const originalWarn = console.warn;
      const originalError = console.error;
      const originalLog = console.log;
      const suppressWarnings = () => {
        console.warn = (...args: any[]) => {
          const message = args.join(' ');
          if (message.includes('willReadFrequently') || message.includes('Canvas2D')) {
            return;
          }
          originalWarn.apply(console, args);
        };
        console.error = (...args: any[]) => {
          const message = args.join(' ');
          if (message.includes('willReadFrequently') || message.includes('Canvas2D')) {
            return;
          }
          originalError.apply(console, args);
        };
        console.log = (...args: any[]) => {
          const message = args.join(' ');
          if (message.includes('willReadFrequently') || message.includes('Canvas2D')) {
            return;
          }
          originalLog.apply(console, args);
        };
      };
      const restoreConsole = () => {
        console.warn = originalWarn;
        console.error = originalError;
        console.log = originalLog;
      };

      suppressWarnings();
      
      const { default: QrScanner } = await import('qr-scanner');
      
      setTimeout(async () => {
        try {
          const scannerContainer = document.getElementById('other-header-qr-scanner-container');
          if (!scannerContainer) {
            restoreConsole();
            throw new Error('Scanner container not found');
          }

          scannerContainer.innerHTML = '';
          
          const videoElem = document.createElement('video');
          videoElem.style.width = '100%';
          videoElem.style.height = '100%';
          videoElem.style.objectFit = 'cover';
          scannerContainer.appendChild(videoElem);
          
          const qrScanner = new QrScanner(
            videoElem,
            (result) => {
              restoreConsole();
              // In this context we just show the result as we don't have the full handler logic here
              toast({
                title: "QR Code Scanned",
                description: `Result: ${result.data}`,
              });
              qrScanner.stop();
              setShowScanner(false);
              setIsScanning(false);
              qrScannerInstanceRef.current = null;
            },
            {
              onDecodeError: () => {},
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );
          
          qrScannerInstanceRef.current = qrScanner;
          await qrScanner.start();
        } catch (innerError) {
          restoreConsole();
          console.error('Error in QR scanner initialization:', innerError);
          toast({
            variant: "destructive",
            title: "Scanner Error",
            description: "Could not initialize QR scanner.",
          });
          setShowScanner(false);
          setIsScanning(false);
        }
      }, 100);
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      toast({
        variant: "destructive",
        title: "Scanner Error",
        description: "Could not access camera for QR scanning.",
      });
      setShowScanner(false);
      setIsScanning(false);
    }
  };

  return (
    <header 
      className={`fixed top-3 left-0 right-0 z-50 transition-all duration-300 ${
        !isVisible ? '-translate-y-full' : 'translate-y-0'
      } pointer-events-none`}
    >
      <div className="max-w-5xl mx-auto px-4">
        <div
          className={`pointer-events-auto flex items-center justify-between h-12 sm:h-14 rounded-xl border border-border/80 shadow-lg px-3 sm:px-4 ${
            isScrolled 
              ? 'bg-background/85 backdrop-blur-md' 
              : 'bg-background/60 backdrop-blur-md'
          }`}
        >
          {/* Logo Section */}
          <div className="flex items-center space-x-2">
            <Link to="/" className="flex items-center gap-2 group">
              <div 
                className="w-6 h-6 sm:w-8 sm:h-8"
                style={{
                  maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  backgroundColor: 'hsl(var(--primary))',
                  WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                }}
                role="img"
                aria-label="Metefy Logo"
              />
              <span className="inline-block text-base sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                Metefy
              </span>
            </Link>
          </div>

          {/* Right actions: Sidebar only */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              className="rounded-lg text-foreground hover:bg-transparent hover:text-primary"
              onClick={() => setIsSheetOpen(true)}
            >
              <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>

            <TopDrawer open={isSheetOpen} onOpenChange={setIsSheetOpen} translucent={false}>
              <div className="flex flex-col h-full text-foreground">
                {/* Drawer Header */}
                <div className="flex items-center justify-center h-14 sm:h-16 px-4">
                  <Link to="/" className="flex items-center gap-2" onClick={() => setIsSheetOpen(false)}>
                    <div 
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      style={{
                        maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                        maskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        maskPosition: 'center',
                        backgroundColor: 'hsl(var(--primary))',
                        WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                        WebkitMaskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center',
                      }}
                      role="img"
                      aria-label="Metefy Logo"
                    />
                    <span className="text-lg sm:text-xl font-bold text-foreground">
                      Metefy
                    </span>
                  </Link>
                </div>

                {/* Navigation */}
                <nav className="py-3 shrink-0">
                  <div className="space-y-1">
                    {navigationItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={() => setIsSheetOpen(false)}
                        className={`w-full px-4 py-2.5 md:py-3 text-left rounded-lg flex items-center gap-3 transition-all duration-300 ${
                          isActive(item.to)
                            ? 'bg-primary/10 text-primary border-l-4 border-primary'
                            : 'text-foreground/80 hover:text-foreground hover:bg-primary/10 hover:border-l-4 hover:border-primary/50'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </nav>

                {/* Social and Action Icons */}
                <div className="px-4 pt-1 pb-3 shrink-0">
                    <div className="grid grid-cols-5 gap-3 w-full">
                      {/* Column 1: YouTube + Instagram */}
                      <div className="flex flex-col items-center gap-2">
                        <a
                          href="https://www.youtube.com/@mete_fy"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Subscribe on YouTube"
                          className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-red-400/50 flex items-center justify-center"
                        >
                          <YouTubeIcon className="w-4 h-4" />
                        </a>
                        <a
                          href="https://instagram.com/mete_fy"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Follow us on Instagram"
                          className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-pink-400/50"
                        >
                          <InstagramIcon className="w-4 h-4" />
                        </a>
                      </div>

                      {/* Column 2: Call + X */}
                      <div className="flex flex-col items-center gap-2">
                        <a
                          href="tel:+918307562996"
                          aria-label="Call Metefy"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-emerald-400/50 flex items-center justify-center"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                        <a
                          href="https://x.com/mete_fy"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Follow us on X"
                          className="bg-black hover:bg-gray-800 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-gray-400/50"
                        >
                          <XSvg className="w-4 h-4" />
                        </a>
                      </div>

                      {/* Column 3: Telegram + WhatsApp */}
                      <div className="flex flex-col items-center gap-2">
                        <a
                          href="https://t.me/+918307562996?text=Hello%20!%20I'm%20interested%20in%20Metefy%20QR%20stickers."
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Contact on Telegram"
                          className="bg-sky-600 hover:bg-sky-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-sky-400/50 flex items-center justify-center"
                        >
                          <TelegramIcon className="w-4 h-4" />
                        </a>
                        <a
                          href="https://wa.me/918307562996?text=Hello%20!%20I'm%20interested%20in%20Metefy%20QR%20stickers."
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Contact us on WhatsApp"
                          className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-green-400/50"
                        >
                          <WhatsAppIcon className="w-4 h-4" />
                        </a>
                      </div>

                      {/* Column 4: Settings + Facebook */}
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() => {
                            setIsThemeDialogOpen(true);
                            setIsSheetOpen(false);
                          }}
                          aria-label="Customize theme"
                          className="bg-primary hover:bg-primary/90 text-primary-foreground p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-primary/50 flex items-center justify-center"
                        >
                          <Palette className="w-4 h-4" />
                        </button>
                        <a
                          href="https://www.facebook.com/profile.php?id=61575578170519"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Follow us on Facebook"
                          className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-blue-400/50"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.407.593 24 1.325 24h11.495v-9.294H9.691v-3.622h3.129V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.796.715-1.796 1.763v2.312h3.592l-.468 3.622h-3.124V24h6.127C23.407 24 24 23.407 24 22.674V1.326C24 .593 23.407 0 22.675 0z"/></svg>
                        </a>
                      </div>

                      {/* Column 5: Scan + Pinterest */}
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() => {
                            startQrScan();
                            setIsSheetOpen(false);
                          }}
                          aria-label="Scan QR Code"
                          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-cyan-400/50 flex items-center justify-center"
                        >
                          <Scan className="w-4 h-4" />
                        </button>
                        <a
                          href="https://www.pinterest.com/mete_fy"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Follow us on Pinterest"
                          className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-red-400/50"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 0C5.395 0 .08 5.315.08 11.96c0 4.96 3.091 9.188 7.456 10.905-.103-.927-.195-2.346.04-3.358.213-.91 1.37-5.79 1.37-5.79s-.35-.71-.35-1.757c0-1.646.954-2.876 2.143-2.876 1.01 0 1.497.758 1.497 1.666 0 1.015-.647 2.531-.982 3.937-.279 1.167.592 2.118 1.755 2.118 2.106 0 3.721-2.22 3.721-5.427 0-2.835-2.036-4.822-4.942-4.822-3.367 0-5.345 2.523-5.345 5.135 0 1.018.393 2.111.885 2.704.097.117.111.22.084.339-.093.373-.304 1.166-.345 1.328-.053.213-.173.259-.4.157-1.493-.695-2.426-2.879-2.426-4.64 0-3.77 2.74-7.234 7.9-7.234 4.145 0 7.364 2.951 7.364 6.896 0 4.117-2.594 7.432-6.196 7.432-1.21 0-2.346-.628-2.734-1.37l-.744 2.836c-.27 1.04-1.003 2.342-1.495 3.138 1.123.348 2.314.536 3.552.536 6.646 0 11.96-5.314 11.96-11.96C23.92 5.315 18.686 0 12.04 0z"/></svg>
                        </a>
                      </div>
                    </div>
                  </div>

                {/* Action Buttons */}
                <div className="px-4 pt-3 pb-2 md:py-3 space-y-2.5 md:space-y-3 border-t border-border/60 shrink-0">
                  <Button
                    onClick={() => {
                      setIsStickerDrawerOpen(true);
                      setIsSheetOpen(false);
                    }}
                    className="w-full bg-foreground hover:bg-foreground/90 text-background transition-all duration-300"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Stickers
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  {user ? (
                    <Button
                      asChild
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 hover:shadow-lg"
                    >
                      <Link
                        to="/profile"
                        className="flex items-center justify-center gap-2 group"
                        onClick={() => setIsSheetOpen(false)}
                      >
                        <UserIcon className="w-4 h-4" />
                        Profile
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 group"
                      onClick={() => {
                        setIsAuthDrawerOpen(true);
                        setIsSheetOpen(false);
                      }}
                    >
                      <Rocket className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
                      Start
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  )}
                </div>
              </div>
            </TopDrawer>

          </div>
        </div>
      </div>

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={(open) => {
        if (!open) {
          setShowScanner(false);
          setIsScanning(false);
          if (qrScannerInstanceRef.current) {
            qrScannerInstanceRef.current.stop();
            qrScannerInstanceRef.current.destroy();
            qrScannerInstanceRef.current = null;
          }
          const scannerContainer = document.getElementById('other-header-qr-scanner-container');
          if (scannerContainer) {
            scannerContainer.innerHTML = '';
          }
        }
      }}>
        <DialogContent className="bg-card border-border w-[90%] sm:w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%] max-w-md rounded-lg sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">Scan QR Code</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Point your camera at a Metefy QR code
            </DialogDescription>
          </DialogHeader>
          <div 
            id="other-header-qr-scanner-container" 
            className="w-full h-64 bg-muted rounded-lg flex items-center justify-center"
          >
            {isScanning ? (
              <div className="text-center">
                <QrCode className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Initializing camera...</p>
              </div>
            ) : (
              <div className="text-center">
                <QrCode className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Camera ready</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowScanner(false);
                setIsScanning(false);
                if (qrScannerInstanceRef.current) {
                  qrScannerInstanceRef.current.stop();
                  qrScannerInstanceRef.current.destroy();
                  qrScannerInstanceRef.current = null;
                }
                const scannerContainer = document.getElementById('other-header-qr-scanner-container');
                if (scannerContainer) {
                  scannerContainer.innerHTML = '';
                }
              }}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Theme Dialog */}
      <ThemeDialog isOpen={isThemeDialogOpen} setIsOpen={setIsThemeDialogOpen} />
      
      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
      <AboutOwnerProfile
        isOpen={isDemoProfileOpen}
        setIsOpen={setIsDemoProfileOpen}
        profile={getDemoProfile() as any}
        copyTweetTemplate={() => {}}
        isDemo={true}
      />
      <MetefyQrAuthDrawer isOpen={isAuthDrawerOpen} setIsOpen={setIsAuthDrawerOpen} />
    </header>
  );
} 
